import { makeAutoObservable } from 'mobx'
import { GapAutocompleteSingleProps } from './GapAutocompleteSingle'
import { GapAutocompleteData, GapAutocompleteOption, GapAutocompleteOptionExtended } from '../types/GapAutocompleteTypes'
import { gapAutocompleteNormaliseOptions } from '../shared/GapAutocompleteShared'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'

interface GapAutocompleteSingleStoreProps extends Pick<GapAutocompleteSingleProps, 'options' | 'value' | 'onChange' | 'onNewOption'> { }

export class GapAutocompleteSingleStore {
   sp
   /** Raw text currently typed into the autocomplete input (separate from the selected option's value). */
   inputValue = ''
   /**
    * Whether the footer (not an option) is the active/highlighted row. MUI keeps the last-hovered option
    * focused even after the pointer leaves the list, so this lets the footer take over the highlight and
    * hold it when the pointer leaves via the footer.
    */
   isFooterActive = false

   constructor(sp: GapAutocompleteSingleStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get selectedOptions() {
      return this.findOptionById(this.sp.options, this.sp.value)
   }

   /**
    * Footer is active while the pointer is over it, an option otherwise. Driven by a single mouseover on
    * the dropdown Paper; when the pointer leaves the Paper the last value sticks, so leaving via the footer
    * keeps it highlighted and leaving via the options keeps MUI's normal option highlight.
    */
   setFooterActiveFromPointer = (event: React.MouseEvent<HTMLElement>) => {
      this.isFooterActive = !!(event.target as HTMLElement).closest('[data-gap-autocomplete-footer]')
   }

   /** Keyboard navigation re-activates an option. */
   onHighlightChange = (_event: React.SyntheticEvent, _option: GapAutocompleteOption | null, reason: string) => {
      if (reason === 'keyboard') {
         this.isFooterActive = false
      }
   }

   /** Reset when the dropdown closes so the next open starts clean. */
   onClose = () => {
      this.isFooterActive = false
   }

   /**
    * Keep focus on the input when pressing the footer. Without this, mousedown on the footer blurs the
    * input and closes the dropdown before the footer's click fires, so the click is lost.
    */
   preventBlur = (event: React.MouseEvent) => {
      event.preventDefault()
   }

   onInputChange = (_: React.SyntheticEvent<Element, Event>, value: string) => {
      this.inputValue = value
   }

   onChange = (_: React.SyntheticEvent<Element, Event>, value: string | GapAutocompleteOption | null) => {
      if (typeof value === 'string') {
         // Call the onNewOption callback if it's a custom input, not selecting an existing option
         // This assumes you have an onNewOption callback in your store props for handling new options
         this.sp.onNewOption?.(value);
      }
      // Handling selection of an existing option or a "+ Create" option
      else if (value && 'id' in value) {
         if (value.specialOption && value.inputValue) {
            // If it's a "+ Create" option, use the inputValue to call onNewOption
            this.sp.onNewOption?.(value.inputValue);
         } else {
            // Otherwise, it's a selection of an existing option, so call onChange with the option's id
            this.sp.onChange?.(value.id);
         }
      }
      // Trigger onChange with null value
      else {
         this.sp.onChange?.(null)
      }
   }

   getOptionLabel = (option: string | GapAutocompleteOptionExtended): string => {
      if (typeof option === 'string') {
         return option
      }

      if (option.inputValue) {
         return option.inputValue
      }

      return option.label
   }

   findOptionById = (options: GapAutocompleteData, id?: string): GapAutocompleteOptionExtended | undefined => {
      if (!id) {
         return undefined
      }

      return gapAutocompleteNormaliseOptions(options).find(option => id.includes(option.id))
   }
}

export const { Provider: GapAutocompleteSingleStoreProvider, useProvidedStore: useGapAutocompleteSingleStore } = createStoreContext(GapAutocompleteSingleStore)